import { useSyncExternalStore } from 'react';

/**
 * Pending-password-setup state for invite and recovery links (Slice P0-E).
 *
 * WHY THIS IS NOT DRIVEN BY THE AUTH EVENT ALONE — measured, not assumed. Running the real
 * @supabase/supabase-js against both link shapes emits:
 *
 *   type=invite    -> ['INITIAL_SESSION', 'SIGNED_IN']
 *   type=recovery  -> ['INITIAL_SESSION', 'PASSWORD_RECOVERY']
 *
 * `PASSWORD_RECOVERY` therefore covers recovery links ONLY; an invited user is
 * indistinguishable from an ordinary sign-in by event alone. The SDK confirms it: all three
 * emission sites branch on `type === 'recovery'` (GoTrueClient `_initialize`, `verifyOtp`,
 * the PKCE exchange). So the invite case is detected from the URL instead.
 *
 * ORDERING: `detectSessionInUrl` makes supabase-js consume and CLEAR `window.location.hash`
 * the moment the client is first constructed. `capturePendingAuthFlow()` must therefore run
 * before the first `getSupabase()` call — it is invoked at the top of main.tsx, and
 * `getSupabase()` is only ever reached from React effects, which run after that.
 *
 * PERSISTENCE: sessionStorage, not memory — a reload on the set-password screen would
 * otherwise drop the flag (the hash is already gone) and let the user into the app without
 * ever choosing a password. sessionStorage survives the reload and dies with the tab.
 *
 * SCOPE LIMIT (stated plainly): this tracks "arrived through an invite/recovery link", NOT
 * "this account has no password" — the latter is not observable from the client. A user who
 * abandons the tab and signs in later with a password they were separately given is not
 * re-prompted.
 */
export type PendingAuthFlow = 'invite' | 'recovery';

const STORAGE_KEY = 'app.pending-password-setup';

const listeners = new Set<() => void>();
// Mirrors sessionStorage so useSyncExternalStore has a stable, synchronous snapshot source
// (reading storage on every getSnapshot would allocate a fresh value and loop).
let current: PendingAuthFlow | null = null;

function isPendingAuthFlow(value: unknown): value is PendingAuthFlow {
  return value === 'invite' || value === 'recovery';
}

function readStorage(): PendingAuthFlow | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return isPendingAuthFlow(raw) ? raw : null;
  } catch {
    // Storage can be unavailable (private mode, blocked cookies) — degrade to in-memory only.
    return null;
  }
}

function writeStorage(value: PendingAuthFlow | null): void {
  try {
    if (value === null) {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } else {
      window.sessionStorage.setItem(STORAGE_KEY, value);
    }
  } catch {
    // Ignored deliberately: the in-memory value still gates this tab's navigation.
  }
}

function emit(): void {
  for (const listener of listeners) listener();
}

function set(value: PendingAuthFlow | null): void {
  if (current === value) return;
  current = value;
  writeStorage(value);
  emit();
}

/** `#type=invite` (implicit flow) or `?type=invite`; anything else is not a setup link. */
function readTypeFromUrl(): PendingAuthFlow | null {
  const fromHash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const fromQuery = new URLSearchParams(window.location.search);
  const raw = fromHash.get('type') ?? fromQuery.get('type');
  return isPendingAuthFlow(raw) ? raw : null;
}

/**
 * Reads the landing URL and restores any flag persisted by an earlier load of this tab.
 * Call once, before the Supabase client exists.
 */
export function capturePendingAuthFlow(): PendingAuthFlow | null {
  current = readStorage();
  const fromUrl = readTypeFromUrl();
  if (fromUrl !== null) set(fromUrl);
  return current;
}

/** Secondary trigger: the `PASSWORD_RECOVERY` event, which fires for recovery links. */
export function markPendingPasswordSetup(flow: PendingAuthFlow): void {
  set(flow);
}

/** Called once the password has actually been set, and on sign-out. */
export function clearPendingPasswordSetup(): void {
  set(null);
}

export function getPendingPasswordSetup(): PendingAuthFlow | null {
  return current;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Reactive view of the flag — the guard re-renders the moment it is set or cleared. */
export function usePendingPasswordSetup(): PendingAuthFlow | null {
  return useSyncExternalStore(subscribe, getPendingPasswordSetup, () => null);
}
